import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  linkWithCredential,
  EmailAuthProvider,
  AuthError,
} from "firebase/auth";
import { getDB, getFirebaseAuth } from "../Firebase";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { USERS_COLLECTION, getDefaultProfile } from "./FirebaseUsers";

export interface AuthResult {
  ok: boolean;
  uid?: string;
  error?: string;
}

function mapAuthError(code: string): string {
  const MAP: Record<string, string> = {
    "auth/email-already-in-use":      "Email уже используется",
    "auth/invalid-email":             "Некорректный email",
    "auth/weak-password":             "Пароль слишком короткий (минимум 6 символов)",
    "auth/user-not-found":            "Пользователь не найден",
    "auth/wrong-password":            "Неверный пароль",
    "auth/too-many-requests":         "Слишком много попыток. Подождите.",
    "auth/network-request-failed":    "Ошибка сети. Проверьте подключение.",
    "auth/invalid-credential":        "Неверный email или пароль",
    "auth/operation-not-allowed":     "Регистрация по email отключена. Обратитесь к администратору.",
    "auth/unauthorized-domain":       "Домен не разрешён. Добавьте nardy.io в Firebase Auth.",
    "auth/credential-already-in-use": "Этот аккаунт уже существует. Войдите вместо регистрации.",
    "auth/provider-already-linked":   "Email уже привязан к аккаунту.",
    "auth/requires-recent-login":     "Требуется повторный вход.",
    "auth/popup-closed-by-user":      "Окно входа было закрыто.",
    "auth/internal-error":            "Внутренняя ошибка Firebase. Попробуйте позже.",
  };
  if (!code) return "Неизвестная ошибка. Попробуйте снова.";
  return MAP[code] ?? `Ошибка: ${code}`;
}

export async function register(
  email: string,
  password: string,
  nickname: string
): Promise<AuthResult> {
  try {
    const auth = getFirebaseAuth(); // initialize() уже был вызван ранее через signIn()
    const db   = getDB();
    let uid: string;

    // Если текущая сессия анонимная — привязываем к ней email
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      const cred   = EmailAuthProvider.credential(email, password);
      const linked = await linkWithCredential(auth.currentUser, cred);
      uid = linked.user.uid;
    } else {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      uid = result.user.uid;
    }

    const ref  = doc(db, USERS_COLLECTION, uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // Новый пользователь — создаём полный профиль
      const profile = getDefaultProfile(uid, nickname);
      await setDoc(ref, {
        ...profile,
        email,
        createdAt: serverTimestamp(),
        lastSeen:  serverTimestamp(),
      });
    } else {
      // Апгрейд гостя: email и nickname могут отсутствовать в документе
      const existing = snap.data();
      const needsUpdate: Record<string, any> = {};
      if (!existing.email) needsUpdate.email = email;
      if (!existing.nickname && nickname) needsUpdate.nickname = nickname;
      if (Object.keys(needsUpdate).length > 0) {
        const { updateDoc } = await import("firebase/firestore");
        await updateDoc(ref, needsUpdate);
      }
    }

    return { ok: true, uid };
  } catch (err: any) {
    return { ok: false, error: mapAuthError((err as AuthError).code ?? "") };
  }
}

export async function login(email: string, password: string): Promise<AuthResult> {
  try {
    const auth   = getFirebaseAuth();
    const result = await signInWithEmailAndPassword(auth, email, password);
    return { ok: true, uid: result.user.uid };
  } catch (err: any) {
    return { ok: false, error: mapAuthError((err as AuthError).code ?? "") };
  }
}

export async function resetPassword(email: string): Promise<AuthResult> {
  try {
    await sendPasswordResetEmail(getFirebaseAuth(), email);
    return { ok: true };
  } catch (err: any) {
    return { ok: false, error: mapAuthError((err as AuthError).code ?? "") };
  }
}
